import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { ArrowLeft, HelpCircle, Search, MessageCircle, Phone, Mail, User, ShoppingBasket, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const CentralAjuda = () => {
  const { siteSettings } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);

  const siteName = siteSettings?.site_name || 'Nossa Feira';

  const faqItems = [
    {
      id: 1,
      category: 'Geral',
      question: `Como funciona o ${siteName}?`,
      answer: `O ${siteName} é uma plataforma que conecta produtores rurais locais diretamente aos consumidores. Os produtores listam seus produtos frescos e os consumidores podem navegar, avaliar e entrar em contato diretamente via WhatsApp para fazer suas compras.`
    },
    {
      id: 2,
      category: 'Consumidores',
      question: 'Como faço para comprar produtos?',
      answer: 'Para comprar, navegue pelo marketplace, encontre o produto desejado e clique no botão "Falar com Produtor". Você será redirecionado para o WhatsApp onde pode negociar diretamente com o produtor sobre preços, quantidades, entrega e pagamento.'
    },
    {
      id: 3,
      category: 'Consumidores',
      question: 'Como posso avaliar um produto?',
      answer: 'Após comprar e experimentar um produto, faça login na sua conta, vá até a página do produto e clique em "Avaliar". Você pode dar uma nota de 1 a 5 estrelas e escrever um comentário sobre sua experiência.'
    },
    {
      id: 4,
      category: 'Produtores',
      question: 'Como posso me tornar um produtor na plataforma?',
      answer: 'Para se tornar um produtor, você precisa entrar em contato com nossa equipe administrativa. Apenas administradores podem criar contas de produtores para garantir a qualidade e autenticidade dos produtos oferecidos.'
    },
    {
      id: 5,
      category: 'Produtores',
      question: 'Como adiciono produtos para venda?',
      answer: 'Após ter sua conta de produtor criada, faça login e acesse o painel do produtor. Clique em "Novo Produto" e preencha as informações: nome, preço, quantidade, categoria, descrição e localização. Todos os produtos passam por aprovação administrativa antes de aparecer no marketplace.'
    },
    {
      id: 6,
      category: 'Produtores',
      question: 'Como configuro formas de pagamento?',
      answer: 'No painel do produtor, acesse a aba "Configurações". Lá você pode selecionar quais formas de pagamento aceita: PIX, cartão de débito, cartão de crédito e suas bandeiras preferidas. Essas informações aparecerão nos seus produtos para os consumidores.'
    },
    {
      id: 7,
      category: 'Conta',
      question: 'Esqueci minha senha, como recupero?',
      answer: 'Na página de login, clique em "Esqueci minha senha". Digite seu telefone cadastrado e siga as instruções enviadas para redefinir sua senha.'
    },
    {
      id: 8,
      category: 'Conta',
      question: 'Como atualizo minhas informações pessoais?',
      answer: 'Entre em contato conosco através dos canais de suporte com as informações que deseja alterar. Nossa equipe fará as atualizações necessárias em seu perfil.'
    },
    {
      id: 9,
      category: 'Pagamentos',
      question: 'A plataforma processa pagamentos?',
      answer: `Não, o ${siteName} não processa pagamentos. Somos apenas uma plataforma de conexão. Os pagamentos são feitos diretamente entre consumidor e produtor através dos métodos acordados entre eles (PIX, dinheiro, cartão, etc.).`
    },
    {
      id: 10,
      category: 'Entrega',
      question: 'Como funciona a entrega dos produtos?',
      answer: 'A entrega é combinada diretamente entre o consumidor e o produtor via WhatsApp. Pode ser retirada na propriedade, entrega local, ou outro acordo entre as partes. A plataforma não gerencia entregas.'
    }
  ];

  const categories = [...new Set(faqItems.map(item => item.category))];

  const filteredFAQs = faqItems.filter(item => 
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
          
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <HelpCircle className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Central de Ajuda</h1>
            <p className="text-gray-600">Encontre respostas para suas dúvidas sobre o {siteName}</p>
          </div>

          {/* Search */}
          <div className="relative max-w-md mx-auto mb-8 flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-gray-500 z-10 pointer-events-none" />
            <Input
              placeholder="Buscar ajuda..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Contact Options */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          <Card className="card-feira text-center">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">WhatsApp</h3>
              <p className="text-sm text-gray-600 mb-3">Fale conosco diretamente</p>
              <a 
                href="https://wa.me/5577999999999" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-600 hover:underline text-sm font-medium"
              >
                {siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}
              </a>
            </CardContent>
          </Card>

          <Card className="card-feira text-center">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Email</h3>
              <p className="text-sm text-gray-600 mb-3">Envie sua dúvida por email</p>
              <a 
                href={`mailto:${siteSettings?.contact_email || 'contato@nossafeira.com'}`}
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                {siteSettings?.contact_email || 'contato@nossafeira.com'}
              </a>
            </CardContent>
          </Card>

          <Card className="card-feira text-center">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-semibold mb-2">Telefone</h3>
              <p className="text-sm text-gray-600 mb-3">Ligue para nossa equipe</p>
              <a 
                href="tel:+5577999999999"
                className="text-orange-600 hover:underline text-sm font-medium"
              >
                {siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}
              </a>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Categories */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Perguntas Frequentes</h2>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((category) => (
              <span
                key={category}
                className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 border border-gray-200"
              >
                {category}
              </span>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFAQs.map((faq) => (
              <Card key={faq.id} className="card-feira">
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {faq.category}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                      </div>
                      {openFAQ === faq.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {openFAQ === faq.id && (
                    <div className="px-6 pb-6">
                      <div className="border-t pt-4">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Nenhum resultado encontrado</h3>
              <p className="text-gray-500">Tente buscar com outros termos ou entre em contato conosco diretamente.</p>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <Card className="card-feira">
          <CardHeader>
            <CardTitle>Links Úteis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/termos-de-uso" className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Settings className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Termos de Uso</span>
              </Link>
              
              <Link to="/politica-de-privacidade" className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <User className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Política de Privacidade</span>
              </Link>
              
              <Link to="/cookies" className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Settings className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Política de Cookies</span>
              </Link>
              
              <Link to="/marketplace" className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <ShoppingBasket className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Ir ao Marketplace</span>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Still Need Help */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Ainda precisa de ajuda?</h3>
          <p className="text-gray-600 mb-6">Nossa equipe está pronta para ajudar você!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${siteSettings?.contact_email || 'contato@nossafeira.com'}`}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Enviar Email
            </a>
            <a
              href="https://wa.me/5577999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CentralAjuda;